import type { ControlConfiguration } from '../CompositionSettings/types';

/**
 * PLAT-10913 — control types whose value is a single string leaf, and are
 * therefore translatable / quick-editable. `aiText` qualifies identically to
 * `text` (`property: 'text'`, one `elementPaths` entry); the only difference
 * is who writes the value. This is a type-list widening, not a loosening of
 * the single-string-leaf rule.
 */
function isSingleStringLeafType(type: ControlConfiguration['type']): boolean {
  return type === 'text' || type === 'aiText';
}

export type TranslationFlagWarning =
  | {
      kind: 'missing_id';
      controlLabel: string;
      allowTranslation: boolean;
      allowQuickEdit: boolean;
    }
  | {
      kind: 'non_text_translatable';
      controlId: string;
      controlLabel: string;
      controlType: string;
    }
  | {
      kind: 'non_text_quick_edit';
      controlId: string;
      controlLabel: string;
      controlType: string;
    }
  | {
      kind: 'translations_without_flag';
      controlId: string;
      controlLabel: string;
    }
  | {
      kind: 'multi_path_translatable';
      controlId: string;
      controlLabel: string;
      pathCount: number;
    };

export interface ValidateTranslationFlagsResult {
  sanitized: ControlConfiguration[];
  warnings: TranslationFlagWarning[];
}

export function validateTranslationFlags(
  controls: ControlConfiguration[],
): ValidateTranslationFlagsResult {
  const warnings: TranslationFlagWarning[] = [];
  const sanitized = controls.map(control => {
    // PLAT-10506 — text controls default to translatable + quick-editable when
    // the author left the flag unset; an explicit `false` opts out. Producers
    // almost always want overlay / standalone text shown and translatable, so
    // "unset" means "yes". Only text controls that carry an id are defaulted —
    // id-less or non-text controls keep the explicit-only reading so we don't
    // emit `missing_id` / `non_text_*` demotion warnings for fields that never
    // opted in.
    const defaultsToTrue = control.type === 'text' && !!control.id;
    const allowTranslation = defaultsToTrue
      ? control.allowTranslation !== false
      : !!control.allowTranslation;
    const allowQuickEdit = defaultsToTrue
      ? control.allowQuickEdit !== false
      : !!control.allowQuickEdit;
    const hasTranslations =
      !!control.translations && Object.keys(control.translations).length > 0;
    const next: ControlConfiguration = {
      ...control,
      allowTranslation,
      allowQuickEdit,
    };

    if ((allowTranslation || allowQuickEdit) && !control.id) {
      warnings.push({
        kind: 'missing_id',
        controlLabel: control.label,
        allowTranslation,
        allowQuickEdit,
      });
      next.allowTranslation = false;
      next.allowQuickEdit = false;
    }

    if (next.allowTranslation && !isSingleStringLeafType(control.type)) {
      warnings.push({
        kind: 'non_text_translatable',
        controlId: control.id ?? '',
        controlLabel: control.label,
        controlType: control.type,
      });
      next.allowTranslation = false;
    }

    if (next.allowQuickEdit && !isSingleStringLeafType(control.type)) {
      warnings.push({
        kind: 'non_text_quick_edit',
        controlId: control.id ?? '',
        controlLabel: control.label,
        controlType: control.type,
      });
      next.allowQuickEdit = false;
    }

    if (hasTranslations && !next.allowTranslation) {
      warnings.push({
        kind: 'translations_without_flag',
        controlId: control.id ?? '',
        controlLabel: control.label,
      });
      next.translations = undefined;
    }

    if (next.allowTranslation && control.elementPaths.length > 1) {
      warnings.push({
        kind: 'multi_path_translatable',
        controlId: control.id ?? '',
        controlLabel: control.label,
        pathCount: control.elementPaths.length,
      });
    }

    return next;
  });

  return { sanitized, warnings };
}
